import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';

import { ChatComponent } from '../chat/chat.component';
import { GlobalAssistDrawerService } from '../../services/global-assist-drawer.service';
import { DefineFacade, GlobalChatFacade, WorkspaceFacade } from '@state';

@Component({
  selector: 'app-global-chat-drawer',
  standalone: true,
  imports: [ChatComponent],
  templateUrl: './global-chat-drawer.component.html',
  styleUrl: './global-chat-drawer.component.scss',
})
export class GlobalChatDrawerComponent implements OnInit {
  private readonly elRef = inject(ElementRef<HTMLElement>);
  private readonly drawer = inject(GlobalAssistDrawerService);
  protected readonly globalChat = inject(GlobalChatFacade);
  protected readonly store = inject(WorkspaceFacade);
  private readonly defineStore = inject(DefineFacade);
  private readonly router = inject(Router);

  protected readonly isOpen = this.drawer.isOpen;
  protected readonly widthPx = signal(400);
  private readonly widthStorageKey = 'globalAssist.drawerWidthPx';
  private readonly minW = 280;
  private dragStartX = 0;
  private dragStartWidth = 0;
  protected isDragging = false;

  readonly chatContext = { page: 'global' };

  readonly orchestratorPageContext = computed(() => {
    const pid = this.store.activeProjectId();
    const projects = this.store.projects() ?? [];
    const name = pid ? String(projects.find((p) => p?.id === pid)?.name ?? '').trim() : '';
    return {
      project: pid ? { id: pid, name: name || 'Project' } : null,
      currentRoute: this.router.url,
      definition: this.defineStore.definition(),
      repoContext: this.defineStore.repoContext(),
    };
  });

  constructor() {
    effect(() => {
      const open = this.drawer.isOpen();
      const pid = this.store.activeProjectId();
      if (open && pid) {
        void this.globalChat.load();
      }
    });
  }

  ngOnInit(): void {
    try {
      const raw = localStorage.getItem(this.widthStorageKey);
      if (raw) {
        const n = Number(raw);
        if (Number.isFinite(n)) {
          this.widthPx.set(this.clampWidth(n));
        }
      }
    } catch {
      /* ignore */
    }
  }

  private clampWidth(w: number): number {
    const max = Math.max(this.minW, Math.floor(window.innerWidth * 0.88));
    return Math.min(max, Math.max(this.minW, Math.floor(w)));
  }

  private persistWidth(): void {
    try {
      localStorage.setItem(this.widthStorageKey, String(this.widthPx()));
    } catch {
      /* ignore */
    }
  }

  protected close(): void {
    const root = this.elRef.nativeElement;
    const active = document.activeElement;
    if (active instanceof HTMLElement && root.contains(active)) {
      active.blur();
    }
    this.drawer.close();
    queueMicrotask(() => {
      document.querySelector<HTMLButtonElement>('button[aria-label="Open assistant"]')?.focus();
    });
  }

  protected onResizePointerDown(event: PointerEvent): void {
    event.preventDefault();
    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.dragStartWidth = this.widthPx();
    try {
      (event.target as HTMLElement).setPointerCapture(event.pointerId);
    } catch {
      /* ignore */
    }
  }

  @HostListener('document:pointermove', ['$event'])
  protected onDocumentPointerMove(event: PointerEvent): void {
    if (!this.isDragging) return;
    const delta = event.clientX - this.dragStartX;
    this.widthPx.set(this.clampWidth(this.dragStartWidth + delta));
  }

  @HostListener('document:pointerup', ['$event'])
  @HostListener('document:pointercancel', ['$event'])
  protected onDocumentPointerUp(_event: PointerEvent): void {
    if (!this.isDragging) return;
    this.isDragging = false;
    this.persistWidth();
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    if (this.drawer.isOpen()) {
      this.close();
    }
  }

  protected async onMessagesChange(messages: Array<{ role: 'user' | 'assistant'; text: string }>) {
    await this.globalChat.setChatMessages(messages);
  }

  protected async onMessageCreated(msg: { role: 'user' | 'assistant'; text: string }) {
    await this.globalChat.logChatEvent(msg.text, msg.role);
  }
}
